// src/components/Inicio/MisEstadisticas.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { isSuperAdmin } from "../../services/authUtils";
import sensorService from "../../services/sensorService";
import registroSensoresService from "../../services/registroSensoresService";

const REFRESH_KEY = "mis-estadisticas:refreshMs";
const DEFAULT_REFRESH = 5 * 60 * 1000; // 5 minutos

const MisEstadisticas = () => {
  const { user } = useAuth();
  const esSuper = useMemo(
    () => !!isSuperAdmin() || !!user?.superUsuario,
    [user?.superUsuario]
  );

  // ===== Estado base =====
  const [estruct, setEstruct] = useState([]); // estanques -> dispositivos -> sensores
  const [series, setSeries] = useState([]);   // [{id, nombre, unidad, estanque, dispositivo, registros:[{t, valor}]}]
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  // Rango: último día por defecto
  const [desde, setDesde] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 16);
  });
  const [hasta, setHasta] = useState(() => new Date().toISOString().slice(0, 16));

  // Auto-refresh (persistente en localStorage, resetea a 5 min por sesión nueva)
  const [refreshMs, setRefreshMs] = useState(() => {
    const saved = Number(window.localStorage.getItem(REFRESH_KEY));
    return Number.isFinite(saved) ? saved : DEFAULT_REFRESH;
  });
  useEffect(() => {
    window.localStorage.setItem(REFRESH_KEY, String(refreshMs));
  }, [refreshMs]);

  // ===== Estructura de sensores accesibles =====
  const sensoresAccesibles = useMemo(() => {
    const out = [];
    (estruct || []).forEach((e) => {
      (e.dispositivos || []).forEach((d) => {
        (d.sensores || []).forEach((s) => {
          out.push({
            id: s.id,
            nombre: s.nombre,
            unidad: s.unidad,
            tipo: s.tipoParametroNombre,
            dispositivo: d.dispositivoNombre,
            estanque: e.estanqueNombre,
          });
        });
      });
    });
    // evitar duplicados
    const seen = new Set();
    return out.filter((s) => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });
  }, [estruct]);

  // ===== Traer estructura base =====
  const cargarEstructura = useCallback(async () => {
    try {
      const resp = await sensorService.obtenerTodosSensores();
      setEstruct(resp || []);
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar la lista de sensores.");
    }
  }, []);

  // ===== Boost: primeras 3 páginas para no cargar “poco” =====
  const firstLoadBoostRef = useRef(true);

  // Resetea boost cuando cambia el rango
  useEffect(() => {
    firstLoadBoostRef.current = true;
  }, [desde, hasta]);

  // ===== Traer datos (admin o normal) =====
  const fetchData = useCallback(async () => {
    setCargando(true);
    setError("");

    try {
      // Asegurar ISO completos
      const desdeIso = new Date(desde).toISOString();
      const hastaIso = new Date(hasta).toISOString();

      if (esSuper) {
        // ---- SUPERADMIN: un solo hit a buscarAdmin y agrupamos por sensor ----
        const page = 1;
        const pageSize = (firstLoadBoostRef.current ? 3 : 1) * 200; // 3 páginas * 200 por defecto
        const payload = {
          usuarioId: null,
          estanqueId: null,
          sensorId: null,
          q: "",
          desdeUtc: desdeIso,
          hastaUtc: hastaIso,
          page,
          pageSize,
          desc: true, // del más reciente al más antiguo
        };
        const r = await registroSensoresService.buscarAdmin(payload);
        // Orden defensivo
        const items = (r.items || []).slice().sort((a, b) =>
          new Date(b.fechaRegistroUtc).getTime() - new Date(a.fechaRegistroUtc).getTime()
        );

        // Agrupar por sensorId
        const bySensor = new Map();
        for (const it of items) {
          const id = it.sensorId ?? it.sensor?.id ?? it.sensorNombre ?? "s/desconocido";
          if (!bySensor.has(id)) {
            bySensor.set(id, {
              id,
              nombre: it.sensorNombre ?? `Sensor ${id}`,
              unidad: it.unidad ?? "",
              tipo: it.tipoParametroNombre ?? "",
              dispositivo: it.dispositivoNombre ?? "",
              estanque: it.estanqueNombre ?? "",
              registros: [],
            });
          }
          bySensor.get(id).registros.push({
            t: new Date(it.fechaRegistroUtc),
            valor: Number(it.valor),
          });
        }

        // Pasar a array y normalizar orden (ascendente para el trazo)
        const arr = Array.from(bySensor.values()).map((s) => ({
          ...s,
          registros: s.registros
            .slice()
            .sort((a, b) => a.t.getTime() - b.t.getTime())
            .map((r) => ({ fecha: r.t.toLocaleString(), valor: r.valor })),
        }));

        setSeries(arr);
      } else {
        // ---- NO SUPER: pedir por sensor con endpoint normal (no admin) ----
        const page = 1;
        const pageSize = (firstLoadBoostRef.current ? 3 : 1) * 200;

        const promises = sensoresAccesibles.map(async (s) => {
          try {
            // usamos el endpoint “buscar” (no admin)
            const r = await registroSensoresService.buscar({
              sensorId: s.id,
              desdeUtc: desdeIso,
              hastaUtc: hastaIso,
              page,
              pageSize,
              desc: true,
            });

            const items = (r.items || []).slice().sort(
              (a, b) =>
                new Date(b.fechaRegistroUtc).getTime() -
                new Date(a.fechaRegistroUtc).getTime()
            );

            const registros = items
              .slice()
              .reverse() // ascendente para el trazo
              .map((it) => ({
                fecha: new Date(it.fechaRegistroUtc).toLocaleString(),
                valor: Number(it.valor),
              }));

            return { ...s, registros };
          } catch (e) {
            // si algún sensor falla, lo omitimos
            console.warn("Fallo al traer registros de sensor", s.id, e);
            return { ...s, registros: [] };
          }
        });

        const res = await Promise.all(promises);
        setSeries(res.filter((s) => s.registros.length > 0));
      }
    } catch (e) {
      console.error(e);
      setError("No se pudieron obtener los datos.");
    } finally {
      firstLoadBoostRef.current = false;
      setCargando(false);
    }
  }, [esSuper, desde, hasta, sensoresAccesibles]);

  // Primera carga
  useEffect(() => {
    cargarEstructura();
  }, [cargarEstructura]);

  useEffect(() => {
    // cuando hay estructura, traemos datos
    if (estruct) fetchData();
  }, [estruct, fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (!refreshMs || refreshMs <= 0) return; // Apagado
    const id = setInterval(() => fetchData(), refreshMs);
    return () => clearInterval(id);
  }, [refreshMs, fetchData]);

  // ====== Estadísticas ======
  const calcularEstadisticas = (vals) => {
    if (!vals?.length) return null;
    const n = vals.length;
    const sorted = [...vals].sort((a, b) => a - b);
    const sum = sorted.reduce((acc, v) => acc + v, 0);
    const mean = sum / n;
    const min = sorted[0];
    const max = sorted[n - 1];
    const median = n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;

    const counts = new Map();
    for (const v of sorted) counts.set(v, (counts.get(v) || 0) + 1);
    let mode = sorted[0], best = 0;
    for (const [k, c] of counts) if (c > best) { best = c; mode = k; }

    const variance = sorted.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / n;
    const stdDev = Math.sqrt(variance);

    return { min, max, mean, median, mode, stdDev, count: n };
  };

  // ====== UI ======
  return (
    <div className="bg-neutral-900 text-white rounded-lg p-4 sm:p-6 w-full max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl sm:text-2xl font-bold text-green-400">Mis Estadísticas</h2>
        <div className="flex items-center gap-2">
          <label className="text-xs sm:text-sm flex items-center gap-2">
            <span className="text-neutral-300">Actualizar cada:</span>
            <select
              value={refreshMs}
              onChange={(e) => setRefreshMs(Number(e.target.value))}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs sm:text-sm"
              title="Auto-refresco de esta pestaña"
            >
              <option value={0}>Apagado</option>
              <option value={30_000}>30 s</option>
              <option value={60_000}>1 min</option>
              <option value={120_000}>2 min</option>
              <option value={300_000}>5 min</option>
              <option value={600_000}>10 min</option>
            </select>
          </label>
          {esSuper && (
            <span className="text-xs border border-emerald-500/60 px-2 py-1 rounded text-emerald-300">
              Superadministrador
            </span>
          )}
        </div>
      </div>

      {/* Filtros de rango */}
      <div className="mt-4 flex flex-col sm:flex-row items-center gap-2">
        <input
          type="datetime-local"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
        />
        <span className="text-slate-400 text-sm">→</span>
        <input
          type="datetime-local"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
        />
        <button
          onClick={fetchData}
          className="bg-emerald-700 hover:bg-emerald-600 text-white px-3 py-1 rounded text-sm"
        >
          Actualizar
        </button>
      </div>

      {cargando && <div className="mt-4 text-slate-300">Cargando…</div>}
      {!cargando && error && <div className="mt-4 text-red-400">{error}</div>}

      {!cargando && !error && series.length > 0 && (
        <div className="mt-6 space-y-8">
          {series.map((s) => {
            const valores = s.registros.map((r) => r.valor);
            const stats = calcularEstadisticas(valores);

            return (
              <div key={s.id} className="text-center">
                <h3 className="text-lg font-semibold text-cyan-300 mb-2">
                  {s.estanque} / {s.dispositivo} / {s.nombre} {s.unidad ? `(${s.unidad})` : ""}
                </h3>

                <div className="bg-slate-800 border border-slate-700 rounded-lg p-3">
                  <div className="mx-auto" style={{ maxWidth: 1000 }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={s.registros}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="fecha" hide />
                        <YAxis stroke="#67e8f9" />
                        <Tooltip
                          contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155" }}
                          labelStyle={{ color: "#e2e8f0" }}
                          formatter={(v) => [v, "Valor"]}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="valor" stroke="#10b981" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {stats && (
                    <div className="mt-4 flex justify-center">
                      <table className="text-sm border-collapse border border-slate-600">
                        <thead>
                          <tr className="bg-slate-700 text-emerald-300">
                            <th className="px-2 py-1 border border-slate-600">Mín</th>
                            <th className="px-2 py-1 border border-slate-600">Máx</th>
                            <th className="px-2 py-1 border border-slate-600">Media</th>
                            <th className="px-2 py-1 border border-slate-600">Mediana</th>
                            <th className="px-2 py-1 border border-slate-600">Moda</th>
                            <th className="px-2 py-1 border border-slate-600">Desv. Est.</th>
                            <th className="px-2 py-1 border border-slate-600">N</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="px-2 py-1 border border-slate-600">{stats.min.toFixed(2)}</td>
                            <td className="px-2 py-1 border border-slate-600">{stats.max.toFixed(2)}</td>
                            <td className="px-2 py-1 border border-slate-600">{stats.mean.toFixed(2)}</td>
                            <td className="px-2 py-1 border border-slate-600">{stats.median.toFixed(2)}</td>
                            <td className="px-2 py-1 border border-slate-600">{String(stats.mode)}</td>
                            <td className="px-2 py-1 border border-slate-600">{stats.stdDev.toFixed(2)}</td>
                            <td className="px-2 py-1 border border-slate-600">{stats.count}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!cargando && !error && series.length === 0 && (
        <div className="mt-6 text-center text-slate-400">
          No hay datos disponibles en el rango seleccionado.
        </div>
      )}
    </div>
  );
};

export default MisEstadisticas;
